import { createSlice } from "@reduxjs/toolkit";
import { getState } from "@bd-stores/store";

/**
 * @type {BD_StoreGameProgress}
 */
const initialState = {
    players: [],
    isFinished: false,
    fossilEnergy: 0,
    greenEnergy: 0,
    currentRound: 0,
    roundStartedAt: 0,
    roundShouldEndAt: 0,
    roundStatus: {},
    fossilPerRound: {},
    greenPerRound: {},
    score: 0,
};

const gameProgressSlice = createSlice({
    name: "gameProgress",
    initialState,
    reducers: {
        /**
         * @param {typeof initialState} state
         * @param {import("@reduxjs/toolkit").PayloadAction<BD_StoreGameProgress['players']>} action
         */
        setPlayers: (state, action) => ({
            ...state,
            players: action.payload,
        }),

        /**
         * @param {typeof initialState} state
         * @param {import("@reduxjs/toolkit").PayloadAction<BD_StoreGameProgress['isFinished']>} action
         */
        setFinished: (state, action) => ({
            ...state,
            isFinished: action.payload,
        }),

        /**
         * @param {typeof initialState} state
         * @param {import("@reduxjs/toolkit").PayloadAction<BD_StoreGameProgress['roundStatus']>} action
         */
        setRoundStatus: (state, action) => ({
            ...state,
            roundStatus: action.payload,
        }),

        setFossilEnergy: (state, action) => ({
            ...state,
            fossilEnergy: action.payload,
        }),

        setFossilPerRound: (state, action) => ({
            ...state,
            fossilPerRound: action.payload,
        }),

        setGreenEnergy: (state, action) => ({
            ...state,
            greenEnergy: action.payload,
        }),

        setGreenPerRound: (state, action) => ({
            ...state,
            greenPerRound: action.payload,
        }),

        setCurrentRound: (state, action) => ({
            ...state,
            currentRound: action.payload,
        }),

        setRoundStartedAt: (state, action) => ({
            ...state,
            roundStartedAt: action.payload,
        }),

        setRoundShouldEndAt: (state, action) => ({
            ...state,
            roundShouldEndAt: action.payload,
        }),

        setScore: (state, action) => ({
            ...state,
            score: action.payload,
        }),
        
    },
});

export const getPlayers = () => {
    const players = getState().gameProgress.players;
    return players ?? [];
};

export const getRoundStatus = (roundId) => {
    const roundStatus = getState().gameProgress.roundStatus;
    return roundStatus?.[roundId] ?? 'unstarted';
};

export const getFossilPerRound = (roundId) => {
    const fossilPerRound = getState().gameProgress.fossilPerRound;
    return fossilPerRound?.[roundId] ?? 0;
};

export const getGreenPerRound = (roundId) => {
    const greenPerRound = getState().gameProgress.greenPerRound;
    return greenPerRound?.[roundId] ?? 0;
};

export const getFossilEnergy = () => {
    const fossilEnergy = getState().gameProgress.fossilEnergy;
    return fossilEnergy ?? 0;
};

export const getGreenEnergy = () => {
    const greenEnergy = getState().gameProgress.greenEnergy;
    return greenEnergy ?? 0;
};

export const getCurrentRound = () => {
    const currentRound = getState().gameProgress.currentRound;
    return currentRound ?? 0;
};

export const getRoundStartedAt = () => {
    const roundStartedAt = getState().gameProgress.roundStartedAt;
    return roundStartedAt ?? 0;
};

export const getRoundShouldEndAt = () => {
    const roundShouldEndAt = getState().gameProgress.roundShouldEndAt;
    return roundShouldEndAt ?? 0;
};

export const getUserFromId = (userId) => {
    const players = getState().gameProgress.players;
    const user = players.find((p) => p.userId === userId);
    return user;
};

export const getScore = () => {
    const score = getState().gameProgress.score;
    return score ?? 0;
};

export const {
    setScore,
    setPlayers,
    setFinished,
    setRoundStatus,
    setFossilEnergy,
    setGreenEnergy,
    setCurrentRound,
    setRoundStartedAt,
    setRoundShouldEndAt,
    setFossilPerRound,
    setGreenPerRound,
} = gameProgressSlice.actions;
export const gameProgress = gameProgressSlice.reducer;
